// Code-generated pixel art. Everything is drawn into small canvases, then handed to Pixi as textures.
import { Texture } from 'pixi.js';
import { CITY_ART, VehicleType, VEHICLES, NpcKind } from '../../common/src/data';
import { GameMap, TILE } from '../../common/src/map';
import { paintCity } from './city-art';
import type { CityLight } from './blocks';
import { PALETTE } from '../../common/src/rules';
import { hash2 } from '../../common/src/util';
import { CREW } from './crew';
import { PALETTE as THEME } from './theme';
import { canvas } from './canvas';
import { npcTop, PED_VARIANTS, ABBOT_SKIN, type NpcStyle } from './npc-detail';

export const C = {
  void: '#05030a', night: '#0b0614', deep: '#1a0f2e', purple: '#2a1a45', violet: '#4a2a78',
  teal: '#2de2c8', tealDim: '#16786c', pink: '#ff3ea5', pinkDim: '#8a1f5c', yellow: '#ffe03a', acid: '#d8ff3a',
  red: '#ff3e6c', white: '#e8e8f0', grey: '#6a6480',
};
/** Six street paints (random, respray), then the Kaminari-gumi's hot pink (config.biker.paint = 6); nothing else picks index 6. */
export const PAINTS = ['#c8324a', '#2d6fd6', '#e0c040', '#5a5a6a', '#8a3ad6', '#2aa88a', THEME.pink];

export { canvas };
export const tex = (c: HTMLCanvasElement | ImageBitmap) => { const t = Texture.from(c); t.source.scaleMode = 'nearest'; return t; };
/** Readability: wrap a sprite in a 1px dark outline so it separates from busy scenery. */
function outlined(src: HTMLCanvasElement, col = '#05030a'): HTMLCanvasElement {
  const [c, g] = canvas(src.width + 2, src.height + 2);
  const [m, mg] = canvas(src.width, src.height); mg.drawImage(src, 0, 0); mg.globalCompositeOperation = 'source-in'; mg.fillStyle = col; mg.fillRect(0, 0, src.width, src.height);
  for (const [dx, dy] of [[0, 1], [2, 1], [1, 0], [1, 2]]) g.drawImage(m, dx, dy);
  g.drawImage(src, 1, 1); return c;
}
const px = (g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, col: string) => { g.fillStyle = col; g.fillRect(x, y, w, h); };

// ------------------------------------------------------------------ map
export function renderMap(m: GameMap, lights: CityLight[] = []): HTMLCanvasElement {
  const [c, g] = canvas(m.w * TILE, m.h * TILE);
  paintCity(g, m, lights);
  return c;
}
/** The painted city cut into CHUNK-px textures: stays under small GPU texture limits and lets the renderer skip
 *  the parts of the 4096x2304 map that are off screen. */
const CHUNK = 1024;
export function mapChunks(m: GameMap, lights: CityLight[] = []): { tex: Texture; x: number; y: number }[] {
  const big = renderMap(m, lights), out: { tex: Texture; x: number; y: number }[] = [];
  for (let y = 0; y < big.height; y += CHUNK) for (let x = 0; x < big.width; x += CHUNK) {
    const [c, g] = canvas(Math.min(CHUNK, big.width - x), Math.min(CHUNK, big.height - y));
    g.drawImage(big, x, y, c.width, c.height, 0, 0, c.width, c.height);
    out.push({ tex: tex(c), x, y });
  }
  big.width = big.height = 0; // release the full-size canvas
  return out;
}

// ------------------------------------------------------------------ glow + fx
const glowCache = new Map<string, Texture>();
export function glowTex(size = 64): Texture {
  const k = `g${size}`; const hit = glowCache.get(k); if (hit) return hit;
  const [c, g] = canvas(size, size);
  const grd = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grd.addColorStop(0, 'rgba(255,255,255,0.9)'); grd.addColorStop(0.3, 'rgba(255,255,255,0.35)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, size, size);
  const t = Texture.from(c); glowCache.set(k, t); return t;
}
export function pixelTex(): Texture { const [c, g] = canvas(1, 1); px(g, 0, 0, 1, 1, '#fff'); return tex(c); }
export function scanlineTex(): Texture {
  const [c, g] = canvas(1, 360);
  for (let y = 0; y < 360; y += 2) px(g, 0, y, 1, 1, `rgba(0,0,0,${CITY_ART.effects.scanlines})`);
  return tex(c);
}
export function fogTex(): Texture {
  const [c, g] = canvas(320, 180);
  const grd = g.createRadialGradient(160, 90, 60, 160, 90, 200);
  grd.addColorStop(0, 'rgba(10,4,24,0)'); grd.addColorStop(1, `rgba(8,15,29,${CITY_ART.effects.fog})`);
  g.fillStyle = grd; g.fillRect(0, 0, 320, 180);
  return Texture.from(c);
}
/** Stepped reflection strokes keep the pixel grid instead of smooth oval puddles. */
export function reflectionTex(): Texture {
  const [c, g] = canvas(26, 10);
  for (const [x, y, w] of [[4, 1, 14], [1, 3, 21], [6, 5, 17], [3, 7, 13], [9, 9, 9]]) px(g, x, y, w, 1, '#ffffff');
  return tex(c);
}
export function adTex(text: string): Texture {
  const [c, g] = canvas(Math.min(300, text.length * 5 + 8), 9);
  px(g, 0, 0, c.width, 9, '#0a061288');
  g.fillStyle = C.teal; g.font = '7px monospace'; g.textBaseline = 'top'; g.fillText(text, 4, 1);
  return tex(c);
}

// ------------------------------------------------------------------ vehicles
const vehCache = new Map<string, Texture>();
export function vehicleTex(t: VehicleType, paint: number, wrecked = false, gunner = false): Texture {
  const key = `${t}:${paint}:${wrecked}:${gunner}`; const hit = vehCache.get(key); if (hit) return hit;
  const s = VEHICLES[t]; const L = s.len, W = s.wid;
  const [c, g] = canvas(L, W);
  let body = PAINTS[paint % PAINTS.length];
  if (t === 'police') body = '#e8e8f0'; if (t === 'armored') body = '#3a3a48';
  if (t === 'autocab') body = '#e8f4f2';
  if (wrecked) body = '#2a2228';
  if (t === 'kei') {
    // cab at the front (+x), open bed behind it
    const cabX = L - 8;
    px(g, 1, 0, L - 2, W, body); px(g, 0, 1, L, W - 2, body);
    px(g, 1, 0, L - 2, 1, '#00000055'); px(g, 1, W - 1, L - 2, 1, '#00000088');
    px(g, 1, 1, cabX - 2, W - 2, '#2a2430'); // bed floor
    px(g, 1, 1, cabX - 2, 1, shadeHex(body, -40)); px(g, 1, W - 2, cabX - 2, 1, shadeHex(body, -40)); px(g, 1, 1, 1, W - 2, shadeHex(body, -40)); // side rails and tailgate
    px(g, 3, 3, 3, 2, '#5a4a38'); px(g, 7, 5, 3, 2, '#4a5a38'); // cargo
    px(g, cabX, 1, 1, W - 2, '#1a3a4a'); // rear window
    px(g, cabX + 1, 2, 3, W - 4, shadeHex(body, -30)); // cab roof
    px(g, cabX + 4, 2, 2, W - 4, '#1a3a4a'); // windshield
    if (gunner && !wrecked) { px(g, 4, 2, 4, W - 4, '#2d6fd6'); px(g, 5, 3, 3, W - 6, '#e0b090'); px(g, 5, 3, 2, W - 6, '#1a1426'); px(g, 8, W / 2 - 1, 5, 2, '#303040'); } // gunner with a gun
    px(g, L - 1, 1, 1, 2, '#fff6a8'); px(g, L - 1, W - 3, 1, 2, '#fff6a8');
    px(g, 0, 1, 1, 2, C.red); px(g, 0, W - 3, 1, 2, C.red);
    if (wrecked) for (let i = 0; i < 6; i++) px(g, Math.floor(hash2(i, L) * L), Math.floor(hash2(W, i) * W), 2, 1, '#0a0608');
  } else if (t === 'bike') {
    px(g, 0, 2, 3, 2, '#222'); px(g, L - 3, 2, 3, 2, '#222');
    px(g, 3, 1, L - 6, 4, body); px(g, 5, 2, 4, 2, '#111'); px(g, L - 5, 1, 2, 4, '#888');
    px(g, L - 1, 2, 1, 2, C.yellow);
  } else {
    px(g, 1, 0, L - 2, W, body); px(g, 0, 1, L, W - 2, body);
    px(g, 1, 0, L - 2, 1, '#00000055'); px(g, 1, W - 1, L - 2, 1, '#00000088');
    const roofX = Math.floor(L * 0.3), roofW = Math.floor(L * 0.4);
    px(g, roofX - 3, 2, 3, W - 4, '#1a3a4a'); // windshield (front is +x)
    px(g, roofX + roofW, 2, 3, W - 4, '#1a3a4a');
    px(g, roofX, 2, roofW, W - 4, shadeHex(body, -30));
    if (t === 'sports') { px(g, 2, 2, L - 6, 1, C.yellow); px(g, L - 7, 3, 4, W - 6, '#1a3a4a'); }
    if (t === 'police') { px(g, roofX + 2, 2, 3, Math.floor((W - 4) / 2), '#2d4fff'); px(g, roofX + 2, 2 + Math.floor((W - 4) / 2), 3, Math.ceil((W - 4) / 2), C.red); px(g, 1, 3, 4, W - 6, '#1a1a2a'); }
    if (t === 'armored') { for (let i = 3; i < L - 3; i += 5) px(g, i, 1, 1, W - 2, '#50506a'); px(g, 1, 1, L - 2, 1, C.yellow); }
    if (t === 'autocab') {
      px(g, 2, 1, L - 4, 1, '#2ab8a8'); px(g, 2, W - 2, L - 4, 1, '#2ab8a8'); // teal side stripes
      px(g, roofX + 2, 3, 3, W - 6, '#2ab8a8'); px(g, roofX + 3, 4, 1, W - 8, '#ffffff'); // sensor dome
      px(g, roofX + roofW - 3, 2, 2, W - 4, C.yellow); px(g, roofX + roofW - 3, 3, 2, 1, '#2d4fff'); // KANEMORI light bar
    }
    px(g, L - 1, 1, 1, 2, '#fff6a8'); px(g, L - 1, W - 3, 1, 2, '#fff6a8'); // headlights
    px(g, 0, 1, 1, 2, C.red); px(g, 0, W - 3, 1, 2, C.red);
    if (wrecked) for (let i = 0; i < 8; i++) px(g, Math.floor(hash2(i, L) * L), Math.floor(hash2(W, i) * W), 2, 1, '#0a0608');
  }
  const tt = tex(outlined(c)); vehCache.set(key, tt); return tt;
}
function shadeHex(hex: string, d: number) {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) + d)), g = Math.max(0, Math.min(255, ((n >> 8) & 255) + d)), b = Math.max(0, Math.min(255, (n & 255) + d));
  return `rgb(${r},${g},${b})`;
}

// ------------------------------------------------------------------ characters (facing +x)
const charCache = new Map<string, Texture>();
/** `crew`: a player's crew mask index (crew.ts CREW), worn over the face. */
/** `npc`/`v`: an NPC outfit and its variant (npc-detail.ts); NPCs never carry `crew`. */
export interface Look { skin: string; jacket: string; hair: string; extra?: 'mask' | 'cap' | 'visor' | 'helmet' | 'bulky'; crew?: number; npc?: NpcStyle; v?: number }
/** `blade`: 'katana', or a crew mask color for the neon tanto held forward. */
export function charTex(l: Look, frame: number, gun: boolean, blade: string | null = null): Texture {
  const katana = blade === 'katana';
  const key = `${l.skin}${l.jacket}${l.hair}${l.extra}${l.crew}${frame}${gun}${blade}`; const hit = charCache.get(key); if (hit) return hit;
  const [c, g] = canvas(14, 14);
  const legOff = frame === 1 ? 2 : frame === 2 ? -2 : 0;
  px(g, 3 + legOff, 4, 3, 2, '#1a1426'); px(g, 3 - legOff, 8, 3, 2, '#1a1426'); // feet
  const bw = l.extra === 'bulky' ? 7 : 6;
  px(g, 4, 7 - bw / 2 - 1, 5, bw + 2, l.jacket); // shoulders
  px(g, 4, 7 - bw / 2 - 1, 5, 1, shadeHex(l.jacket.slice(0, 7), 40));
  px(g, 5, 5, 4, 4, l.skin); // head
  px(g, 5, 5, 3, 4, l.hair);
  if (l.extra === 'cap') { px(g, 5, 5, 3, 4, '#1a2a6a'); px(g, 8, 5, 1, 4, '#e8e8f0'); }
  if (l.extra === 'mask') { px(g, 7, 5, 2, 4, '#e8e8f0'); px(g, 8, 6, 1, 1, C.red); px(g, 8, 8, 1, 1, C.red); }
  if (l.crew !== undefined) { const m = CREW[l.crew]?.color ?? CREW[0].color; px(g, 7, 5, 2, 4, THEME.slate); px(g, 8, 5, 1, 4, m); } // crew mask, face toward +x
  if (l.extra === 'visor') { px(g, 8, 5, 1, 4, C.red); }
  if (l.extra === 'helmet') { px(g, 5, 4, 4, 6, '#2a2a3a'); px(g, 8, 5, 1, 4, C.yellow); }
  if (gun) { px(g, 8, 8, 5, 2, '#303040'); px(g, 12, 8, 1, 1, '#9090a8'); } else { px(g, 8, 3, 2, 2, l.skin); px(g, 8, 9, 2, 2, l.skin); }
  if (katana) { px(g, 10, 9, 1, 2, C.yellow); px(g, 11, 9, 3, 1, '#e8e8f0'); px(g, 11, 10, 3, 1, '#9090a8'); } // guard and a blade held forward from the sword hand
  if (blade && !katana) { px(g, 10, 9, 1, 2, '#1a1426'); px(g, 11, 9, 2, 2, blade); px(g, 13, 9, 1, 1, '#ffffff'); } // short glowing tanto
  const t = tex(outlined(c)); charCache.set(key, t); return t;
}
export function playerLook(ap: [number, number, number], crew?: number): Look {
  return { skin: PALETTE.skin[ap[0]] ?? PALETTE.skin[0], jacket: PALETTE.jacket[ap[1]] ?? PALETTE.jacket[0], hair: PALETTE.hair[ap[2]] ?? PALETTE.hair[0], crew };
}
const PED_JACKETS = ['#5a5468', '#6a5040', '#40506a', '#6a6a50', '#504050', '#3a4a4a'];
/** `gun`: the snapshot's faction gun (NpcSnap.g); it tells Kumadori guards (Smart SMG) and Benikage (none) from Oni-Gumi. */
export function npcLook(k: NpcKind, id: number, gun?: string): Look {
  const base = npcLookFlat(k, id);
  const style: NpcStyle = k === 'gang' ? (gun === 'smart' ? 'kuma' : gun === undefined ? 'oyabun' : 'oni') : k === 'drone' ? 'ped' : k;
  const v = style === 'ped' ? id % PED_VARIANTS : 0;
  return { ...base, jacket: npcTop(style, v), hair: style === 'oyabun' ? THEME.offWhite : base.hair, npc: style, v };
}
/** Kanemori Sanctuary (M10): Shingan (and his decoys, drawn identical on purpose) and his security monks; both are
 *  elites on the server, told apart by the snapshot's `sg`. */
export function sanctLook(who: 'abbot' | 'monk', id: number): Look {
  const base = npcLookFlat('elite', id);
  return { ...base, skin: who === 'abbot' ? ABBOT_SKIN : PALETTE.skin[id % 4], jacket: npcTop(who, 0), hair: base.hair, npc: who, v: 0 };
}
function npcLookFlat(k: NpcKind, id: number): Look {
  switch (k) {
    case 'gang': return { skin: PALETTE.skin[id % 4], jacket: '#b01c3c', hair: '#101018', extra: 'mask' };
    case 'cop': return { skin: PALETTE.skin[id % 4], jacket: '#1c2c6c', hair: '#101018', extra: 'cap' };
    case 'heavy': return { skin: PALETTE.skin[id % 4], jacket: '#141c44', hair: '#101018', extra: 'helmet' };
    case 'guard': return { skin: PALETTE.skin[id % 4], jacket: '#2a2a1a', hair: '#101018', extra: 'helmet' };
    case 'elite': return { skin: '#303040', jacket: '#101014', hair: '#101014', extra: 'visor' };
    case 'biker': return { skin: PALETTE.skin[id % 4], jacket: THEME.pink, hair: THEME.midnight, extra: 'visor' }; // Kaminari-gumi: hot-pink jackets, placeholder art
    default: return { skin: PALETTE.skin[id % 4], jacket: PED_JACKETS[id % PED_JACKETS.length], hair: ['#101018', '#4a3020', '#aaaaaa'][id % 3] };
  }
}
export function droneTex(frame: number, jammed: boolean): Texture {
  const key = `drone${frame}${jammed}`; const hit = charCache.get(key); if (hit) return hit;
  const [c, g] = canvas(14, 14);
  px(g, 5, 5, 4, 4, '#3a3a50'); px(g, 6, 6, 2, 2, jammed ? C.yellow : C.red);
  const r = frame % 2 ? 1 : 0;
  for (const [x, y] of [[1, 1], [10, 1], [1, 10], [10, 10]]) { px(g, x, y + r, 3, 1, '#9090a8'); px(g, x + 1 - r, y - 1 + r * 2, 1, 1, '#9090a8'); }
  px(g, 4, 4, 1, 1, '#50506a'); px(g, 9, 4, 1, 1, '#50506a'); px(g, 4, 9, 1, 1, '#50506a'); px(g, 9, 9, 1, 1, '#50506a');
  const t = tex(outlined(c)); charCache.set(key, t); return t;
}

// ------------------------------------------------------------------ infra + icons
export function iconTex(kind: 'cam' | 'fix' | 'sig' | 'bar' | 'package' | 'cash' | 'weapon' | 'ammo' | 'kiosk'): Texture {
  const key = `icon${kind}`; const hit = charCache.get(key); if (hit) return hit;
  const [c, g] = canvas(10, 10);
  switch (kind) {
    case 'cam': px(g, 1, 3, 6, 4, '#8080a0'); px(g, 7, 4, 2, 2, '#202030'); px(g, 2, 4, 1, 1, C.red); break;
    case 'fix': px(g, 1, 1, 8, 8, '#3a3a48'); px(g, 2, 2, 6, 6, '#50506a'); px(g, 4, 2, 2, 3, C.yellow); px(g, 3, 5, 2, 3, C.yellow); break;
    case 'sig': px(g, 3, 0, 4, 10, '#202028'); break;
    case 'bar': px(g, 0, 3, 10, 4, C.yellow); for (let i = 0; i < 10; i += 4) px(g, i, 3, 2, 4, '#202028'); break;
    case 'package': px(g, 1, 2, 8, 7, '#b07a3a'); px(g, 1, 4, 8, 1, C.yellow); px(g, 4, 2, 2, 7, C.yellow); break;
    case 'cash': px(g, 1, 3, 8, 5, '#3aa84a'); px(g, 4, 4, 2, 3, '#b8ffb8'); break;
    case 'weapon': px(g, 1, 4, 8, 2, '#c0c0d0'); px(g, 2, 6, 2, 3, '#c0c0d0'); break;
    case 'ammo': px(g, 2, 2, 2, 6, '#ffe03a'); px(g, 5, 2, 2, 6, '#ffe03a'); break;
    case 'kiosk': px(g, 2, 1, 6, 8, '#202030'); px(g, 3, 2, 4, 3, C.acid); px(g, 3, 6, 4, 1, C.pink); break;
  }
  const t = tex(outlined(c)); charCache.set(key, t); return t;
}
